<template>
  <div id="contact-page">
    <div id="head-contact" class="bg-grad">
      <h2 class="text-white">{{ $t("contact.title") }}</h2>
    </div>

    <div class="container text-center py-5">
      <h2 class="fw-bold mb-5">{{ $t("contact.intro.title") }}</h2>

      <p>
        {{ $t("contact.intro.p1.beforeStrong1") }}
        <strong>{{ $t("contact.intro.p1.strong1") }}</strong>
        {{ $t("contact.intro.p1.beforeStrong2") }}
        <strong>{{ $t("contact.intro.p1.strong2") }}</strong>
      </p>

      <p>
        {{ $t("contact.intro.p2") }}
      </p>
    </div>

    <!-- Contact-->
    <section class="page-section mb-5" id="contact">
      <div class="container px-4 px-lg-5">
        <div class="row gx-4 gx-lg-5 justify-content-center">
          <div class="col-lg-8 col-xl-6 text-center">
            <p class="text-muted"></p>
          </div>
        </div>

        <div class="row gx-4 gx-lg-5 justify-content-center mb-5">
          <div class="col-lg-6">
            <form
              id="contactForm"
              v-if="!submitted"
              @submit.prevent="submitForm"
            >
              <!-- Lastname input-->
              <div class="form-floating mb-3">
                <input
                  class="form-control"
                  id="lastname"
                  type="text"
                  placeholder=""
                  v-model="data.lastname"
                  @blur="validateField('lastname')"
                  :class="{ 'is-invalid': errors.lastname }"
                />
                <label for="lastname">{{ $t("contact.input1") }}</label>
                <div class="invalid-feedback">
                  {{ errors.lastname }}
                </div>
              </div>

              <!-- Name input-->
              <div class="form-floating mb-3">
                <input
                  class="form-control"
                  id="name"
                  type="text"
                  placeholder=""
                  v-model="data.firstname"
                  @blur="validateField('firstname')"
                  :class="{ 'is-invalid': errors.firstname }"
                />
                <label for="name">{{ $t("contact.input2") }}</label>
                <div class="invalid-feedback">
                  {{ errors.firstname }}
                </div>
              </div>

              <!-- Email address input-->
              <div class="form-floating mb-3">
                <input
                  class="form-control"
                  id="email"
                  type="email"
                  placeholder=""
                  v-model="data.email"
                  @blur="validateField('email')"
                  :class="{ 'is-invalid': errors.email }"
                />
                <label for="email">{{ $t("contact.input4") }}</label>
                <div class="invalid-feedback">
                  {{ errors.email }}
                </div>
              </div>

              <!-- Phone number input-->
              <div class="form-floating mb-3">
                <input
                  class="form-control"
                  id="phone"
                  type="tel"
                  placeholder=""
                  v-model="data.phone"
                  @blur="validateField('phone')"
                  :class="{ 'is-invalid': errors.phone }"
                />
                <label for="phone">{{ $t("contact.input5") }}</label>
                <div class="invalid-feedback">
                  {{ errors.phone }}
                </div>
              </div>

              <!-- Commune input-->
              <div class="form-floating mb-3">
                <input
                  class="form-control"
                  id="commune"
                  type="text"
                  placeholder=""
                  v-model="data.commune"
                  @blur="validateField('commune')"
                  :class="{ 'is-invalid': errors.commune }"
                />
                <label for="commune">{{ $t("contact.communeLabel") }}</label>
                <div class="invalid-feedback">
                  {{ errors.commune }}
                </div>
              </div>

              <!-- Message input-->
              <div class="form-floating mb-3">
                <textarea
                  class="form-control"
                  id="message"
                  placeholder=""
                  style="height: 10rem"
                  v-model="data.message"
                  @blur="validateField('message')"
                  :class="{ 'is-invalid': errors.message }"
                ></textarea>
                <label for="message">{{ $t("contact.input6") }}</label>
                <div class="invalid-feedback">
                  {{ errors.message }}
                </div>
              </div>

              <!-- Submit Button-->
              <div class="d-grid">
                <button
                  class="btn btn-custom btn-xl btn-width mx-auto"
                  id="submitButton"
                  type="submit"
                >
                  {{ $t("contact.button") }}
                </button>
              </div>
            </form>

            <!-- Success Message -->
            <div v-if="submitted" class="alert alert-success mt-4">
              {{ $t("contact.success-message") }}
            </div>

            <!-- Error Message -->
            <div v-if="error" class="alert alert-danger mt-4">
              {{ $t("contact.error-message") }}
            </div>
          </div>
        </div>

        <!-- Info contact -->
        <div class="row gx-4 gx-lg-5 justify-content-center mt-5">
          <div class="col-lg-4 text-center mb-5 mb-lg-0">
            <i class="bi-telephone fs-2 mb-3 text-muted"></i>
            <div>
              <a class="text-muted" href="tel:+32470510391">+32 470 51 03 91</a>
            </div>
          </div>
          <div class="col-lg-4 text-center mb-5 mb-lg-0">
            <i class="bi-envelope fs-2 mb-3 text-muted"></i>
            <div>
              <a class="text-muted" href="mailto:contact@educspe.be"
                >contact@educspe.be</a
              >
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import axios from "axios";

import { useHead } from "@vueuse/head";
import { useI18n } from "vue-i18n";

export default {
  name: "Contact",
  data() {
    return {
      data: {
        firstname: "",
        lastname: "",
        email: "",
        phone: "",
        commune: "",
        message: "",
      },
      errors: {},
      submitted: false,
      error: false,
    };
  },
  methods: {
    validateField(field) {
      if (!this.data[field]) {
        this.errors[field] = this.$t("contact.required");
      } else if (field === "email" && !this.validEmail(this.data.email)) {
        this.errors[field] = this.$t("contact.invalid-email");
      } else {
        delete this.errors[field];
      }
    },
    validEmail(email) {
      const re =
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@(([^<>()[\]\.,;:\s@"]+\.)+[^<>()[\]\.,;:\s@"]{2,})$/i;
      return re.test(String(email).toLowerCase());
    },
    async submitForm() {
      Object.keys(this.data).forEach((field) => this.validateField(field));
      if (Object.values(this.errors).every((error) => !error)) {
        try {
          const Axios = axios.create({
            baseURL: "https://educspe.be",
          });
          await Axios.post("/sendmail.php", this.data);
          this.submitted = true;
          this.error = false;
        } catch (err) {
          this.error = true;
        }
      }
    },
  },
  setup() {
    const { t } = useI18n();

    useHead(() => ({
      title: t("contact.meta-data.title"),
      meta: [
        {
          name: "description",
          content: t("contact.meta-data.desc"),
        },
      ],
    }));
  },
};
</script>

<style scoped>
#head-contact {
  color: white;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-width {
  max-width: 200px;
}

.bg-select {
  background-color: rgb(228, 228, 228);
}
</style>
